import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  useTheme,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  Divider,
  Tooltip,
} from '@mui/material';
import api from '../config/api.js';
import {
  Home as HomeIcon,
  AccountCircle,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import equityLogo from '../assets/equity-logo.png';

const Header = ({ navigate, isMobile }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Fetch admin status from the server (not localStorage)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then(res => { const adminStatus = res.data.is_admin === 1; setIsAdmin(adminStatus); console.log('User admin status:', adminStatus); })
        .catch(() => setIsAdmin(false));
    }
  }, []);

  // Function to refresh user data from localStorage
  const refreshUserData = () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
      }
    }
  };

  // Load user on initial mount
  useEffect(() => {
    refreshUserData();
  }, []);

  // Set up a listener to refresh user data when localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        refreshUserData();
      }
    };

    // Listen for the custom userDataChanged event
    const handleUserDataChanged = () => {
      refreshUserData();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userDataChanged', handleUserDataChanged);

    // Check for changes every 2 seconds as a fallback
    const interval = setInterval(refreshUserData, 2000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataChanged', handleUserDataChanged);
      clearInterval(interval);
    };
  }, []);

  // Mobile styles
  const headerHeight = 64;
  const logoSize = isMobile ? 50 : 64;

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAnalytics = () => {
    handleClose();
    navigate('/analytics');
  };

  const handleDataManagement = () => {
    handleClose();
    navigate('/data-management');
  };

  const handleAgentLifecycle = () => {
    handleClose();
    navigate('/agent-lifecycle');
  };

  const goToHome = () => {
    navigate('/dashboard');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(12px)',
        color: '#1A2B24',
        borderBottom: '1px solid #E0E5E3',
        boxShadow: 'none',
        width: 'calc(100% - 32px)',
        height: headerHeight,
        justifyContent: 'center',
        margin: '16px',
        borderRadius: '16px',
        position: 'fixed',
        top: 0,
        left: 0,
      }}
    >
      <Toolbar sx={{ minHeight: headerHeight, height: headerHeight }}>
        <Tooltip title={t('header.goToDashboard')}>
          <IconButton
            edge="start"
            aria-label="home"
            onClick={goToHome}
            sx={{ mr: 2, color: '#1A2B24' }}
          >
            <HomeIcon />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', height: logoSize }}>
            <img
              src={equityLogo}
              alt={t('header.equityPortalLogo')}
              style={{ height: logoSize, width: 'auto', display: 'block' }}
            />
          </Box>
        </Box>

        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="medium"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
            sx={{
              position: 'relative',
              ml: 1,
            }}
          >
            {user?.profile_picture_url ? (
              <Avatar
                src={user.profile_picture_url}
                sx={{
                  width: 36,
                  height: 36,
                  border: '2px solid',
                  borderColor: '#2C9C6C',
                }}
                alt={user.name || t('header.userProfile')}
              />
            ) : (
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'transparent',
                  color: '#1A2B24',
                }}
              >
                <AccountCircle />
              </Avatar>
            )}
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                backgroundColor: '#FFFFFF',
                border: '1px solid #E0E5E3',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                borderRadius: 2,
                mt: 1,
                minWidth: 180,
              }
            }}
          >
            <MenuItem onClick={handleAnalytics}>
              <ListItemIcon>
                <BarChartIcon fontSize="small" />
              </ListItemIcon>
              {t('header.analytics')}
            </MenuItem>
            {isAdmin && (
              <MenuItem onClick={handleDataManagement}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                {t('header.dataManagement')}
              </MenuItem>
            )}
            {isAdmin && (
              <MenuItem onClick={handleAgentLifecycle}>
                <ListItemIcon>
                  <TimelineIcon fontSize="small" />
                </ListItemIcon>
                {t('header.agentLifecycle')}
              </MenuItem>
            )}

          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
